import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

/**
 * Per-route selection state for cart bulk operations.
 *
 * Held in React context (not a global store) because:
 *   1. Selection is ephemeral — clears on route change is the right UX.
 *   2. Bulk actions act on lines visible *in the same operation
 *      section*; the consumer (BulkBar) needs both the global set
 *      and a filter helper to scope to one process_type.
 *
 * The set stores ``OrderLine.id`` (number). Selection survives line
 * edits because line ids are stable across PATCHes; on delete the FE
 * removes the stale id below.
 *
 * Single-active-operation invariant: only one section can hold a
 * selection at a time. ``toggle`` / ``setMany`` receive the caller's
 * ``opKey`` (process slug). When ``opKey`` differs from ``activeOpKey``,
 * the previous selection is dropped and replaced with the new op's
 * pick — so clicking a checkbox in section B auto-clears section A's
 * highlighted rows. State is held in a single object so the two fields
 * always update atomically.
 */
interface CartSelectionContextValue {
  selected: ReadonlySet<number>;
  activeOpKey: string | null;
  toggle: (lineId: number, opKey: string) => void;
  setMany: (lineIds: number[], on: boolean, opKey: string) => void;
  clear: () => void;
  clearOperation: (opLineIds: readonly number[]) => void;
  countInOperation: (opLineIds: readonly number[]) => number;
}

interface SelectionState {
  selected: Set<number>;
  activeOpKey: string | null;
}

const CartSelectionContext = createContext<CartSelectionContextValue | null>(null);

export function CartSelectionProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const [state, setState] = useState<SelectionState>(() => ({
    selected: new Set<number>(),
    activeOpKey: null,
  }));

  const toggle = useCallback((lineId: number, opKey: string) => {
    setState((prev) => {
      const switchingOp = prev.activeOpKey !== null && prev.activeOpKey !== opKey;
      if (switchingOp) {
        return { selected: new Set([lineId]), activeOpKey: opKey };
      }
      const next = new Set(prev.selected);
      if (next.has(lineId)) next.delete(lineId);
      else next.add(lineId);
      return {
        selected: next,
        activeOpKey: next.size === 0 ? null : opKey,
      };
    });
  }, []);

  const setMany = useCallback(
    (lineIds: number[], on: boolean, opKey: string) => {
      setState((prev) => {
        if (on) {
          const switchingOp =
            prev.activeOpKey !== null && prev.activeOpKey !== opKey;
          if (switchingOp) {
            return { selected: new Set(lineIds), activeOpKey: opKey };
          }
          const next = new Set(prev.selected);
          for (const id of lineIds) next.add(id);
          return {
            selected: next,
            activeOpKey: next.size === 0 ? null : opKey,
          };
        }
        const next = new Set(prev.selected);
        for (const id of lineIds) next.delete(id);
        return {
          selected: next,
          activeOpKey: next.size === 0 ? null : prev.activeOpKey,
        };
      });
    },
    [],
  );

  const clear = useCallback(() => {
    setState({ selected: new Set(), activeOpKey: null });
  }, []);

  const clearOperation = useCallback((opLineIds: readonly number[]) => {
    setState((prev) => {
      const next = new Set(prev.selected);
      for (const id of opLineIds) next.delete(id);
      return {
        selected: next,
        activeOpKey: next.size === 0 ? null : prev.activeOpKey,
      };
    });
  }, []);

  const countInOperation = useCallback(
    (opLineIds: readonly number[]) => {
      let n = 0;
      for (const id of opLineIds) if (state.selected.has(id)) n += 1;
      return n;
    },
    [state.selected],
  );

  const value = useMemo<CartSelectionContextValue>(
    () => ({
      selected: state.selected,
      activeOpKey: state.activeOpKey,
      toggle,
      setMany,
      clear,
      clearOperation,
      countInOperation,
    }),
    [
      state.selected,
      state.activeOpKey,
      toggle,
      setMany,
      clear,
      clearOperation,
      countInOperation,
    ],
  );

  return (
    <CartSelectionContext.Provider value={value}>{children}</CartSelectionContext.Provider>
  );
}

/**
 * Disabled fallback for components that render outside the cart route
 * (e.g. ``MyProjectDetail`` which uses PartCard with selectable=false).
 * Read-only: ``toggle`` and friends are no-ops; ``selected`` is always
 * empty. Callers that need real selection wrap their subtree in
 * ``CartSelectionProvider``.
 */
const DISABLED_SELECTION: CartSelectionContextValue = {
  selected: new Set<number>(),
  activeOpKey: null,
  toggle: () => {},
  setMany: () => {},
  clear: () => {},
  clearOperation: () => {},
  countInOperation: () => 0,
};

export function useCartSelection(): CartSelectionContextValue {
  const ctx = useContext(CartSelectionContext);
  return ctx ?? DISABLED_SELECTION;
}
